import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Briefcase,
  GraduationCap,
  Clock,
  CheckCircle2,
  XCircle,
  Trash2,
  FileText,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import ScoreExplanation from "../components/ScoreExplanation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CandidateDetail() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const candidateRes = await axios.get(`${API}/candidates/${candidateId}`);
      setCandidate(candidateRes.data);

      // Fetch job details
      const jobRes = await axios.get(`${API}/jobs/${candidateRes.data.job_id}`);
      setJob(jobRes.data);
    } catch (error) {
      console.error("Error fetching candidate:", error);
      toast.error("Candidate not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [candidateId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/candidates/${candidateId}`);
      toast.success("Candidate deleted");
      navigate(`/jobs/${candidate.job_id}`);
    } catch (error) {
      console.error("Error deleting candidate:", error);
      toast.error("Failed to delete candidate");
    }
  };

  const getMatchBadgeClass = (level) => {
    if (level === "Strong match") return "badge-strong";
    if (level === "Medium match") return "badge-medium";
    return "badge-low";
  };

  const getScoreCircleClass = (score) => {
    if (score >= 75) return "score-strong";
    if (score >= 50) return "score-medium";
    return "score-low";
  };

  const getProgressColor = (score) => {
    if (score >= 75) return "bg-[#D4F571]";
    if (score >= 50) return "bg-[#FCD34D]";
    return "bg-muted";
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-muted rounded" />
        <div className="h-48 bg-muted rounded-2xl" />
        <div className="h-64 bg-muted rounded-2xl" />
      </div>
    );
  }

  if (!candidate || !job) return null;

  const { data, match_score } = candidate;

  return (
    <div className="space-y-6" data-testid="candidate-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/jobs/${candidate.job_id}`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              {data?.name || "Unknown Candidate"}
            </h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {candidate.file_name}
            </p>
          </div>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="delete-candidate-btn">
              <Trash2 className="w-5 h-5 text-muted-foreground hover:text-destructive" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Candidate</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this candidate? This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Score Overview */}
      <Card data-testid="score-overview">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Main Score */}
            <div className="flex flex-col items-center">
              <div
                className={`score-circle w-24 h-24 text-3xl ${getScoreCircleClass(
                  match_score?.overall_score || 0
                )}`}
              >
                {match_score?.overall_score || 0}
              </div>
              <Badge className={`mt-3 ${getMatchBadgeClass(match_score?.match_level)}`}>
                {match_score?.match_level || "Pending"}
              </Badge>
            </div>

            {/* Score Breakdown */}
            <div className="flex-1 w-full space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Habilidades Requeridas</span>
                  <span className="font-medium">
                    {match_score?.required_skills_score || 0}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full progress-animate ${getProgressColor(
                      match_score?.required_skills_score || 0
                    )}`}
                    style={{ width: `${match_score?.required_skills_score || 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Habilidades Opcionales</span>
                  <span className="font-medium">
                    {match_score?.optional_skills_score || 0}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full progress-animate ${getProgressColor(
                      match_score?.optional_skills_score || 0
                    )}`}
                    style={{ width: `${match_score?.optional_skills_score || 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Experiencia</span>
                  <span className="font-medium">
                    {match_score?.experience_score || 0}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full progress-animate ${getProgressColor(
                      match_score?.experience_score || 0
                    )}`}
                    style={{ width: `${match_score?.experience_score || 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Educación</span>
                  <span className="font-medium">
                    {match_score?.education_score || 0}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full progress-animate ${getProgressColor(
                      match_score?.education_score || 0
                    )}`}
                    style={{ width: `${match_score?.education_score || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score Explanation - NEW FEATURE */}
      <ScoreExplanation 
        matchScore={match_score} 
        candidateData={data} 
        job={job} 
      />

      {/* Original Analysis (kept for additional context) */}
      <Card data-testid="explanation-card">
        <CardHeader>
          <CardTitle className="font-heading">Análisis del Candidato</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {(match_score?.explanation || []).map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                {item.toLowerCase().includes("missing") ||
                item.toLowerCase().includes("may not") ||
                item.toLowerCase().includes("mejorar") ||
                item.toLowerCase().includes("gap") ||
                item.toLowerCase().includes("brecha") ||
                item.toLowerCase().includes("falta") ? (
                  <XCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                )}
                <span className="text-muted-foreground">{item}</span>
              </li>
            ))}
            {(!match_score?.explanation || match_score.explanation.length === 0) && (
              <li className="text-sm text-muted-foreground">
                No hay análisis disponible. El matching fue realizado con el sistema básico.
              </li>
            )}
          </ul>
        </CardContent>
      </Card>

      {/* Candidate Info */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Skills */}
        <Card data-testid="skills-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Skills
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.skills || []).length === 0 ? (
              <p className="text-muted-foreground text-sm">No skills detected</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.skills.map((skill) => {
                  const isRequired = job.required_skills
                    .map((s) => s.toLowerCase())
                    .includes(skill.toLowerCase());
                  const isOptional = job.optional_skills
                    .map((s) => s.toLowerCase())
                    .includes(skill.toLowerCase());
                  return (
                    <span
                      key={skill}
                      className={`skill-tag ${isRequired ? "matched" : ""}`}
                    >
                      {skill}
                      {isRequired && (
                        <CheckCircle2 className="w-3 h-3 ml-1 inline" />
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Missing Required Skills */}
            {job.required_skills.filter(
              (s) =>
                !data.skills.map((sk) => sk.toLowerCase()).includes(s.toLowerCase())
            ).length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground mb-2">
                  Missing Required Skills:
                </p>
                <div className="flex flex-wrap gap-2">
                  {job.required_skills
                    .filter(
                      (s) =>
                        !data.skills
                          .map((sk) => sk.toLowerCase())
                          .includes(s.toLowerCase())
                    )
                    .map((skill) => (
                      <span key={skill} className="skill-tag missing">
                        {skill}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Experience & Education */}
        <div className="space-y-4">
          <Card data-testid="experience-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-heading font-bold">
                {data?.years_experience || 0} years
              </p>
              <p className="text-sm text-muted-foreground">
                Required: {job.min_years_experience || 0}+ years
              </p>

              {(data?.work_experience || []).length > 0 && (
                <ul className="mt-4 space-y-2">
                  {data.work_experience.slice(0, 3).map((exp, i) => (
                    <li key={i} className="text-sm text-muted-foreground">
                      • {exp}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card data-testid="education-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                Education
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data?.education || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No education data detected
                </p>
              ) : (
                <ul className="space-y-2">
                  {data.education.map((edu, i) => (
                    <li key={i} className="text-sm">
                      {edu}
                    </li>
                  ))}
                </ul>
              )}
              {job.education_level && (
                <p className="text-sm text-muted-foreground mt-3">
                  Required: {job.education_level}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Job Reference */}
      <Card>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">Applied for:</p>
          <Button
            variant="link"
            className="p-0 h-auto font-heading font-semibold"
            onClick={() => navigate(`/jobs/${job.id}`)}
            data-testid="view-job-link"
          >
            {job.title}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
